// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { Color } from '../../../../Graphics';
import { PriceType } from '@shared/utils/History/CashItemUtils';
import { ChartDataType } from '@front/chart/Utils/ChartConstants';
import { FractalsAggregationType, FractalsPeriod } from '../../IndicatorConstants';
import { IndicatorScriptBase } from '../../IndicatorScriptBase';
import { LineStyle } from '../../IndicatorScriptBaseEnums';
import { InputParameterInteger } from '../../InputParamaterClasses/InputParameterInteger';
import { InputParameterCombobox } from '../../InputParamaterClasses/InputParameterCombobox';
import { Period } from '../../IndicatorEnums';
export class FractalsBase extends IndicatorScriptBase {
    constructor() {
        super(...arguments);
        this.oneAlert = false;
    }
    initializeFractals(projectName, fractalDimension) {
        this.ProjectName = projectName;
        super.SetIndicatorLine('Fractals Long', Color.MediumBlue, 2, LineStyle.HorizontalLine);
        super.SetIndicatorLine('Fractals Short', Color.MediumBlue, 2, LineStyle.HorizontalLine);
        this.SeparateWindow = false;
        this.fractalDimension = fractalDimension;
        super.InputParameter(new InputParameterInteger('fractalDimension', 'Fractal Dimension', 0, 1, 99999, 0, 1));
        this.fPeriod = FractalsPeriod.Hour;
        super.InputParameter(new InputParameterCombobox('fPeriod', 'Period of fractals', 1, [
            { Second: FractalsPeriod.Second },
            { Minute: FractalsPeriod.Minute },
            { Hour: FractalsPeriod.Hour },
            { Day: FractalsPeriod.Day },
            { Week: FractalsPeriod.Week },
            { Month: FractalsPeriod.Month },
            { Year: FractalsPeriod.Year }
        ]));
        this.agrType = FractalsAggregationType.Current;
        super.InputParameter(new InputParameterCombobox('agrType', 'Aggregation type of fractals', 2, [
            { Current: FractalsAggregationType.Current },
            { Custom: FractalsAggregationType.Custom }
        ]));
        this.value = 3;
        super.InputParameter(new InputParameterInteger('value', 'Value of fractals', 3, 1, 99999, 0, 1));
    }
    Init() {
        this.IndicatorShortName(this.GetIndicatorShortName());
    }
    GetIndicatorShortName() {
        return 'Fractals(' + this.fractalDimension + ')';
    }
    getCustomName() {
        return 'FRACTALS (' + this.ProjectName + ')';
    }
    processFractal(minFractalDimension) {
        if (this.oneAlert) {
            return;
        }
        this.validateInput(minFractalDimension);
        if (this.oneAlert) {
            return;
        }
        const dim = this.calculateDimension();
        if (this.oneAlert) {
            return;
        }
        if (this.CurrentData.Count < dim) {
            return;
        }
        const position = Math.trunc((dim - 1) / 2);
        const fractal = this.calculateFractal(position, dim);
        if (fractal.isHighest) {
            this.SetValue(0, position, this.High(position));
        }
        if (fractal.isLowest) {
            this.SetValue(1, position, this.Low(position));
        }
    }
    validateInput(minFractalDimension) {
        if (this.fractalDimension % 2 === 0 || this.fractalDimension < minFractalDimension) {
            this.showAlert('Fractal Dimension must be an odd number, greater than or equal to ' + minFractalDimension);
            return;
        }
        if (this.CurrentData.cashItem.ChartDataType !== ChartDataType.Default) {
            this.showAlert('Fractals can be built only on the type of aggregation Tick and Time');
        }
    }
    showAlert(message) {
        this.oneAlert = true;
        alert(message);
    }
    calculateDimension() {
        if (this.CurrentData.Period < 0) {
            return Math.trunc(this.value * -this.CurrentData.Period);
        }
        if (this.agrType !== FractalsAggregationType.Custom) {
            return Math.trunc(this.fractalDimension);
        }
        return this.calculateCustomDimension();
    }
    calculateCustomDimension() {
        const minutesInCurrentPeriod = this.CurrentData.Period;
        const minutesInMajorPeriod = this.getMinutesInPeriod(this.fPeriod) * this.value;
        if (!this.oneAlert && minutesInCurrentPeriod > minutesInMajorPeriod) {
            this.showAlert('Please check custom period');
        }
        return Math.trunc(this.fractalDimension * Math.trunc(minutesInMajorPeriod / minutesInCurrentPeriod));
    }
    calculateFractal(position, dim) {
        let isHighest = true;
        let isLowest = true;
        for (let i = dim - 1; i >= 0; i--) {
            if (i === position) {
                continue;
            }
            const comparison = this.compareWithPosition(position, i);
            isHighest = isHighest && comparison.canBeHighest;
            isLowest = isLowest && comparison.canBeLowest;
        }
        return { isHighest, isLowest };
    }
    compareWithPosition(position, i) {
        if (i > position) {
            return {
                canBeHighest: this.High(position) > this.High(i),
                canBeLowest: this.Low(position) < this.Low(i)
            };
        }
        return {
            canBeHighest: this.High(position) >= this.High(i),
            canBeLowest: this.Low(position) <= this.Low(i)
        };
    }
    High(i) {
        return this.CurrentData.getPriceByType(PriceType.High, i);
    }
    Low(i) {
        return this.CurrentData.getPriceByType(PriceType.Low, i);
    }
    getMinutesInPeriod(period) {
        switch (period) {
            case FractalsPeriod.Second:
                return 1;
            case FractalsPeriod.Minute:
                return Period.Min;
            case FractalsPeriod.Hour:
                return Period.Hour;
            case FractalsPeriod.Day:
                return Period.Day;
            case FractalsPeriod.Week:
                return Period.Week;
            case FractalsPeriod.Month:
                return Period.Month;
            case FractalsPeriod.Year:
                return Period.Year;
            default:
                return Period.Hour;
        }
    }
}
